<template>
  <div class="graph-full-map" :class="{ fullscreen: fullscreen }">
    <div class="map-toolbar">
      <div class="toolbar-actions">
        <div class="toolbar-toggles">
          <label class="hover-info-toggle" title="Show or hide instrument nodes in the map">
            <input type="checkbox" v-model="showInstrumentNodes" />
            <span>Show instruments</span>
          </label>
          <label class="hover-info-toggle" title="Show or hide material nodes in the map">
            <input type="checkbox" v-model="showMaterialNodes" />
            <span>Show materials</span>
          </label>
          <label class="hover-info-toggle" title="Show or hide record nodes in the map">
            <input type="checkbox" v-model="showRecordNodes" />
            <span>Show records</span>
          </label>
          <label class="hover-info-toggle" title="Show node and edge details on hover">
            <input type="checkbox" v-model="showHoverInfo" />
            <span>Show hover info</span>
          </label>
        </div>

        <button
          class="btn-secondary"
          :disabled="loading || !hasGraph"
          @click="resetView"
        >
          Reset view
        </button>
        <button
          class="btn-secondary"
          :disabled="loading || !hasGraph"
          @click="resetLayout"
        >
          Reset layout
        </button>
      </div>
      <span class="zoom-indicator" v-if="hasGraph">Zoom: {{ zoomPercent }}%</span>
    </div>

    <div
      ref="containerRef"
      class="map-card"
      @mouseleave="hideTooltip"
    >
      <div v-if="loading" class="state muted">Loading map…</div>
      <div v-else-if="error" class="state error">{{ error }}</div>
      <div v-else-if="!hasGraph" class="state muted">No map data available for this node.</div>

      <svg
        v-else
        ref="svgRef"
        class="map-svg"
        :class="{ 'is-panning': Boolean(panDragState), 'is-interacting': isInteracting }"
        :viewBox="`0 0 ${canvasWidth} ${canvasHeight}`"
        preserveAspectRatio="xMidYMid meet"
        @mousedown="startPanDrag"
        @wheel.prevent="handleWheelZoom"
      >
        <g class="scene-root" :transform="sceneTransform">
          <g class="edges-layer">
            <g
              v-for="edge in edges"
              :key="edge.id"
              class="edge-group"
            >
              <line
                v-if="hasEdgePositions(edge)"
                class="edge-line"
                :x1="nodePosition(edge.src_node_id).x"
                :y1="nodePosition(edge.src_node_id).y"
                :x2="nodePosition(edge.dst_node_id).x"
                :y2="nodePosition(edge.dst_node_id).y"
              />
              <line
                v-if="hasEdgePositions(edge)"
                class="edge-hit"
                :x1="nodePosition(edge.src_node_id).x"
                :y1="nodePosition(edge.src_node_id).y"
                :x2="nodePosition(edge.dst_node_id).x"
                :y2="nodePosition(edge.dst_node_id).y"
                @mousedown.stop
                @click.stop="handleEdgeClick(edge)"
                @mouseenter="showEdgeTooltip(edge, $event)"
                @mousemove="showEdgeTooltip(edge, $event)"
                @mouseleave="hideTooltip"
              />
              <text
                v-if="hasEdgePositions(edge) && showEdgeLabels"
                class="edge-label"
                :x="edgeMidpoint(edge).x"
                :y="edgeMidpoint(edge).y - 10"
                text-anchor="middle"
                :transform="edgeLabelTransform(edge)"
                @mousedown.stop
                @click.stop="handleEdgeClick(edge)"
              >
                {{ edge.relation_type || "related_to" }}
              </text>
            </g>
          </g>

          <g class="nodes-layer">
            <g
              v-for="node in nodes"
              :key="node.id"
              class="node-group"
              :class="{ dragging: dragState && dragState.nodeId === node.id }"
              :transform="`translate(${nodePosition(node.id).x}, ${nodePosition(node.id).y})`"
              @mousedown.stop.prevent="startDrag(node.id, $event)"
              @click.stop="handleNodeClick(node)"
              @mouseenter="showNodeTooltip(node, $event)"
              @mousemove="showNodeTooltip(node, $event)"
              @mouseleave="hideTooltip"
            >
              <circle
                class="node-circle"
                :class="[
                  `node-${node.node_type || 'unknown'}`,
                  { 'is-start': startNode && startNode.id === node.id }
                ]"
                :r="nodeRadius(node)"
              />

              <circle
                v-if="showInstrumentCalibrationRing(node)"
                class="node-calibration-ring"
                :class="nodeCalibrationRingClass(node)"
                :r="nodeCalibrationRingRadius(node)"
              />

              <text v-if="showNodeLabels" class="node-name" text-anchor="middle">
                <tspan x="0" :dy="nodeTertiaryLabel(node) ? '-18' : '-6'">{{ truncate(node.name, 14) }}</tspan>
                <tspan v-if="nodeSecondaryLabel(node)" x="0" dy="18" class="node-type">
                  {{ truncate(nodeSecondaryLabel(node), 14) }}
                </tspan>
                <tspan v-if="nodeTertiaryLabel(node)" x="0" dy="16" class="node-type">
                  {{ truncate(nodeTertiaryLabel(node), 14) }}
                </tspan>
              </text>
            </g>
          </g>
        </g>
      </svg>

      <div
        v-if="tooltip"
        class="graph-tooltip"
        :style="{
          left: `${tooltip.left}px`,
          top: `${tooltip.top}px`
        }"
      >
        <div class="tooltip-title">{{ tooltipTitle }}</div>
        <div
          v-for="row in tooltipRows"
          :key="row.key"
          class="tooltip-row"
        >
          <span class="tooltip-key">{{ row.key }}</span>
          <span class="tooltip-value">{{ row.value }}</span>
        </div>
      </div>

      <div
        v-if="nodePopup.visible"
        class="node-popup-backdrop"
        @click.self="closeNodePopup"
      >
        <div class="node-popup" role="dialog" aria-modal="true" aria-label="Node details">
          <div class="node-popup-header">
            <div class="node-popup-title">{{ nodePopup.node?.name || "Node" }}</div>
            <div class="node-popup-header-actions">
              <button
                class="btn-secondary"
                :disabled="!nodeDetailRoute(nodePopup.node)"
                @click="goToNodePage"
              >
                Go To
              </button>
              <button
                class="btn"
                :disabled="!nodePopup.node || !nodePopupEntityId"
                @click="openAddConnectionPanel"
              >
                Add connection
              </button>
              <button class="btn-secondary" @click="closeNodePopup">Close</button>
            </div>
          </div>

          <div class="node-popup-body">
            <AddGraphConnection
              v-if="nodePopup.showAddConnection && nodePopup.node && nodePopupEntityId"
              :sourceNodeId="nodePopupEntityId"
              @cancel="closeAddConnectionPanel"
              @connected="handleNodeConnectionCreated"
            />

            <div v-if="nodePopup.loading" class="muted">Loading node metadata…</div>
            <div v-else-if="nodePopup.error" class="error">{{ nodePopup.error }}</div>
            <template v-else-if="!nodePopup.showAddConnection">
              <section class="node-popup-section">
                <div
                  v-for="row in popupNodeRows"
                  :key="`popup-${row.key}`"
                  class="node-popup-row"
                >
                  <span class="node-popup-key">{{ row.key }}</span>
                  <span class="node-popup-value">{{ row.value }}</span>
                </div>
              </section>

              <section class="node-popup-section">
                <h4>Templates</h4>
                <div v-if="!nodePopup.mdLists.length" class="muted">No metadata lists linked.</div>

                <div
                  v-for="md in nodePopup.mdLists"
                  :key="`md-${md.mdListId}`"
                  class="node-popup-mdlist"
                >
                  <div class="node-popup-mdlist-title">
                    {{ md.mdList?.name || `MDList ${md.mdListId}` }}
                  </div>

                  <div v-if="md.keyValues && md.keyValues.length" class="node-popup-kv">
                    <div
                      v-for="kv in md.keyValues"
                      :key="`kv-${md.mdListId}-${kv.id || kv.name || kv.keyName}`"
                      class="node-popup-kv-row"
                    >
                      <span class="node-popup-kv-key">{{ kv.name || kv.keyName || "Value" }}</span>
                      <span class="node-popup-kv-value">
                        {{ formatValue(kv.value) }}<span v-if="kv.unit"> ({{ kv.unit }})</span>
                      </span>
                    </div>
                  </div>
                  <div v-else class="muted">No key/value pairs.</div>
                </div>
              </section>
            </template>
          </div>
        </div>
      </div>

      <div
        v-if="edgePopup.visible"
        class="node-popup-backdrop"
        @click.self="closeEdgePopup"
      >
        <div class="node-popup" role="dialog" aria-modal="true" aria-label="Edge details">
          <div class="node-popup-header">
            <div class="node-popup-title">{{ edgePopupTitle }}</div>
            <div class="node-popup-header-actions">
              <button class="btn-secondary" @click="closeEdgePopup">Close</button>
            </div>
          </div>

          <div class="node-popup-body">
            <section class="node-popup-section">
              <div
                v-for="row in edgePopupRows"
                :key="`edge-popup-${row.key}`"
                class="node-popup-row"
              >
                <span class="node-popup-key">{{ row.key }}</span>
                <span class="node-popup-value">{{ row.value }}</span>
              </div>
            </section>

            <section class="node-popup-section" v-if="!isEdgeEditDisabled">
              <h4>Edit relation type</h4>

              <div class="edge-edit-row">
                <select v-model.number="edgePopup.selectedRelationTypeId" class="edge-relation-select">
                  <option :value="null" disabled>Select relation type</option>
                  <option v-for="rt in edgePopup.relationTypes" :key="rt.id" :value="rt.id">
                    {{ rt.name }}{{ rt.category ? ` (${rt.category})` : '' }}
                  </option>
                </select>

                <button
                  type="button"
                  class="btn-secondary edge-plus-btn"
                  title="Create new relation type"
                  :disabled="edgePopup.saving || edgePopup.creatingRelationType"
                  @click="toggleCreateEdgeRelationForm"
                >
                  <i class="bi bi-plus-lg"></i>
                </button>

                <button
                  class="btn"
                  :disabled="!canSaveEdgeRelation"
                  @click="saveEdgeRelationType"
                >
                  {{ edgePopup.saving ? 'Saving…' : 'Save' }}
                </button>
              </div>

              <div v-if="edgePopup.showCreateRelationForm" class="edge-create-form">
                <input
                  type="text"
                  v-model="edgePopup.newRelationTypeName"
                  placeholder="Relation type name"
                  class="edge-create-input"
                />
                <input
                  type="text"
                  v-model="edgePopup.newRelationTypeCategory"
                  placeholder="Category (optional)"
                  class="edge-create-input"
                />
                <div class="edge-create-actions">
                  <button
                    class="btn"
                    :disabled="!edgePopup.newRelationTypeName.trim() || edgePopup.creatingRelationType"
                    @click="createEdgeRelationType"
                  >
                    {{ edgePopup.creatingRelationType ? 'Creating…' : 'Create' }}
                  </button>
                  <button
                    class="btn-secondary"
                    :disabled="edgePopup.creatingRelationType"
                    @click="toggleCreateEdgeRelationForm"
                  >
                    Cancel
                  </button>
                </div>
              </div>

              <div v-if="edgePopup.error" class="error">{{ edgePopup.error }}</div>
            </section>

            <section class="node-popup-section" v-else>
              <div class="muted">Relation type for this edge cannot be edited here.</div>
            </section>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import AddGraphConnection from "@/components/AddGraphConnection.vue";
import apiService from "@/services/apiService";
import { API_ENDPOINTS } from "@/services/config";
import { listEntityMdLists } from "@/services/mdlistBindingService";
import { buildMetadataEntityParams, normalizeMetadataKeyValueCollection } from "@/utils/metadataTransforms";
import { mapState } from "vuex";

const DEFAULT_MARGIN_X = 120;
const DEFAULT_MARGIN_Y = 90;
const MIN_ZOOM = 0.45;
const MAX_ZOOM = 3.5;
const MAX_AUTO_CANVAS_WIDTH = 3200;
const MAX_AUTO_CANVAS_HEIGHT = 2200;
const STRUCTURE_ELEMENT_ID_PATHS = [
  "project_structure_element_id",
  "structure_element_id",
  "projectStructureElementId",
  "structureElementId",
  "project_structure_element.id",
  "structure_element.id",
  "project_structure.id",
  "structure.id",
];
const STRUCTURE_ELEMENT_NAME_PATHS = [
  "project_structure_element_name",
  "structure_element_name",
  "projectStructureElementName",
  "structureElementName",
  "project_structure_element.name",
  "structure_element.name",
  "project_structure.name",
  "structure.name",
];
const STRUCTURE_ELEMENT_ORDER_PATHS = [
  "project_structure_element_order",
  "structure_element_order",
  "projectStructureElementOrder",
  "structureElementOrder",
  "project_structure_element.order",
  "structure_element.order",
  "project_structure.order",
  "structure.order",
];

export default {
  name: "GraphFullMap",
  emits: ["node-drag-end"],
  components: {
    AddGraphConnection,
  },
  props: {
    nodeId: {
      type: Number,
      required: true,
    },
    fullscreen: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      error: "",
      graphData: {
        startNode: null,
        nodes: [],
        edges: [],
      },
      positions: {},
      canvasWidth: 1100,
      canvasHeight: 620,
      zoomBaselineWidth: 1100,
      zoomBaselineHeight: 620,
      zoom: 1,
      panX: 0,
      panY: 0,
      dragState: null,
      panDragState: null,
      interactionFrameId: null,
      pendingDragPosition: null,
      pendingPanPosition: null,
      structureElementContextById: {},
      showHoverInfo: false,
      showInstrumentNodes: true,
      showRecordNodes: true,
      showMaterialNodes: true,
      tooltip: null,
      suppressNodeClick: false,
      nodePopup: {
        visible: false,
        loading: false,
        error: "",
        node: null,
        mdLists: [],
        showAddConnection: false,
      },
      edgePopup: {
        visible: false,
        edge: null,
        relationTypes: [],
        selectedRelationTypeId: null,
        loadingRelationTypes: false,
        saving: false,
        creatingRelationType: false,
        showCreateRelationForm: false,
        newRelationTypeName: "",
        newRelationTypeCategory: "",
        error: "",
      },
    };
  },
  computed: {
    ...mapState(["loggedInUser"]),
    startNode() {
      return this.graphData.startNode;
    },
    allNodes() {
      return this.graphData.nodes;
    },
    nodes() {
      return this.allNodes.filter((node) => this.shouldDisplayNode(node));
    },
    visibleNodeIds() {
      return new Set(this.nodes.map((node) => String(node.id)));
    },
    allEdges() {
      return this.graphData.edges;
    },
    edges() {
      return this.allEdges.filter((edge) => (
        this.visibleNodeIds.has(String(edge.src_node_id))
        && this.visibleNodeIds.has(String(edge.dst_node_id))
      ));
    },
    hasGraph() {
      return this.nodes.length > 0;
    },
    nodeLookup() {
      return this.nodes.reduce((lookup, node) => {
        lookup[node.id] = node;
        return lookup;
      }, {});
    },
    tooltipTitle() {
      if (!this.tooltip) return "";
      if (this.tooltip.kind === "edge") return "Connection details";
      return this.tooltip.payload?.name || "Node details";
    },
    tooltipRows() {
      if (!this.tooltip) return [];

      if (this.tooltip.kind === "edge") {
        const edge = this.tooltip.payload;
        const sourceNode = this.nodeLookup[edge.src_node_id];
        const targetNode = this.nodeLookup[edge.dst_node_id];

        return this.objectEntries({
          ...edge,
          name: edge.name || edge.relation_name || `Connection ${edge.id}`,
          src_node_name: sourceNode ? sourceNode.name : "Unknown",
          dst_node_name: targetNode ? targetNode.name : "Unknown",
        });
      }

      return this.objectEntries(this.buildNodeDetailsPayload(this.tooltip.payload));
    },
    popupNodeRows() {
      if (!this.nodePopup.node) return [];
      return this.objectEntries(this.buildNodeDetailsPayload(this.nodePopup.node));
    },
    sceneTransform() {
      return `translate(${this.panX}, ${this.panY}) scale(${this.zoom})`;
    },
    isInteracting() {
      return Boolean(this.dragState || this.panDragState);
    },
    showNodeLabels() {
      if (this.nodes.length <= 80) return true;
      return this.zoom >= 0.7;
    },
    showEdgeLabels() {
      return true;
    },
    zoomPercent() {
      const baseWidth = this.zoomBaselineWidth || this.canvasWidth || 1;
      const baseHeight = this.zoomBaselineHeight || this.canvasHeight || 1;
      const widthRatio = baseWidth / (this.canvasWidth || 1);
      const heightRatio = baseHeight / (this.canvasHeight || 1);
      const effectiveScaleRatio = Math.min(widthRatio, heightRatio);
      const effectiveZoom = this.zoom * effectiveScaleRatio;
      return Math.round(effectiveZoom * 100);
    },
    nodePopupEntityId() {
      return this.resolveNodeEntityId(this.nodePopup.node);
    },
    edgePopupTitle() {
      return this.edgePopup.edge?.relation_type
        ? `Edge: ${this.edgePopup.edge.relation_type}`
        : "Edge details";
    },
    edgePopupRows() {
      if (!this.edgePopup.edge) return [];
      return this.objectEntries(this.buildEdgeDetailsPayload(this.edgePopup.edge));
    },
    isEdgeEditDisabled() {
      const edge = this.edgePopup.edge;
      if (!edge) return true;

      if (edge.meta && edge.meta.synthetic) return true;

      const parsedId = Number(edge.id);
      return !Number.isFinite(parsedId);
    },
    canSaveEdgeRelation() {
      if (this.isEdgeEditDisabled) return false;
      if (this.edgePopup.saving) return false;
      if (!this.edgePopup.selectedRelationTypeId) return false;
      return String(this.edgePopup.selectedRelationTypeId) !== String(this.edgePopup.edge?.relation_type_id ?? "");
    },
  },
  watch: {
    nodeId: {
      immediate: true,
      handler() {
        this.fetchGraph();
      },
    },
    showHoverInfo(enabled) {
      if (!enabled) {
        this.hideTooltip();
      }
    },
    showInstrumentNodes() {
      this.handleNodeVisibilityToggle();
    },
    showRecordNodes() {
      this.handleNodeVisibilityToggle();
    },
    showMaterialNodes() {
      this.handleNodeVisibilityToggle();
    },
  },
  mounted() {
    window.addEventListener("mousemove", this.handleGlobalMouseMove);
    window.addEventListener("mouseup", this.handleGlobalMouseUp);
  },
  beforeUnmount() {
    window.removeEventListener("mousemove", this.handleGlobalMouseMove);
    window.removeEventListener("mouseup", this.handleGlobalMouseUp);
    if (this.interactionFrameId) {
      cancelAnimationFrame(this.interactionFrameId);
      this.interactionFrameId = null;
    }
  },
  methods: {
    async fetchGraph() {
      this.loading = true;
      this.error = "";
      this.hideTooltip();
      this.dragState = null;

      try {
        const response = await axios.get(API_ENDPOINTS.graphFullMap, {
          params: {
            entra_id: this.loggedInUser?.entra_id,
            start_node_id: this.nodeId,
          },
        });

        const startNode = this.normalizeNode(response.data?.start_node, true);
        const connectedNodes = Array.isArray(response.data?.connected_nodes)
          ? response.data.connected_nodes.map((node) => this.normalizeNode(node))
          : [];
        const edges = Array.isArray(response.data?.edges)
          ? response.data.edges.map((edge) => this.normalizeEdge(edge))
          : [];
        await this.fetchProjectStructureContexts([startNode, ...connectedNodes]);

        const nodesById = {};
        if (startNode) nodesById[startNode.id] = startNode;
        connectedNodes.forEach((node) => {
          nodesById[node.id] = node;
        });

        this.graphData = {
          startNode,
          nodes: Object.values(nodesById),
          edges,
        };

        this.resetLayout();
      } catch (error) {
        console.error(error);
        this.graphData = {
          startNode: null,
          nodes: [],
          edges: [],
        };
        this.positions = {};
        this.error = "Failed to load the full graph map.";
      } finally {
        this.loading = false;
      }
    },

    normalizeNode(node, isStart = false) {
      if (!node || typeof node !== "object") return null;

      const rawEntityId = node.entity_id ?? node.id;
      const parsedEntityId = Number(rawEntityId);

      return {
        ...node,
        id: typeof node.id === "string" ? node.id : Number(node.id),
        entity_id: Number.isFinite(parsedEntityId) ? parsedEntityId : null,
        name: node.name || `Node ${node.id}`,
        node_type: node.node_type || "unknown",
        is_start: isStart,
      };
    },

    normalizeEdge(edge) {
      const parsedId = Number(edge.id);

      return {
        ...edge,
        id: Number.isFinite(parsedId) ? parsedId : String(edge.id),
        src_node_id: typeof edge.src_node_id === "string" ? edge.src_node_id : Number(edge.src_node_id),
        dst_node_id: typeof edge.dst_node_id === "string" ? edge.dst_node_id : Number(edge.dst_node_id),
        relation_type: edge.relation_type || "related_to",
        relation_category: edge.relation_category || "unspecified",
        meta: edge.meta ?? null,
      };
    },

    resolveNodeEntityId(node) {
      const rawId = node?.entity_id ?? node?.id;
      const parsed = Number(rawId);
      return Number.isFinite(parsed) ? parsed : null;
    },

    resolveNodeMetadataId(node) {
      const rawId = node?.node_id ?? node?.id ?? node?.entity_id;
      const parsed = Number(rawId);
      return Number.isFinite(parsed) ? parsed : null;
    },

    resolveNodeProjectId(node) {
      return this.firstFiniteNumber([
        node?.project_id,
        this.firstNumberFromPaths(node, ["project.id", "projectId"]),
      ]);
    },

    normalizeNodeType(node) {
      return String(node?.node_type || node?.kind || "unknown")
        .toLowerCase()
        .trim()
        .replace(/[^a-z_]/g, "")
        .replace(/s$/, "");
    },

    isRecordNode(node) {
      return this.normalizeNodeType(node) === "record";
    },

    isExperimentNode(node) {
      return this.normalizeNodeType(node) === "experiment";
    },

    isInstrumentNode(node) {
      return this.normalizeNodeType(node) === "instrument";
    },

    isMaterialNode(node) {
      return this.normalizeNodeType(node) === "material";
    },

    shouldDisplayNode(node) {
      if (!node) return false;
      if (this.startNode && String(node.id) === String(this.startNode.id)) return true;
      if (this.isInstrumentNode(node) && !this.showInstrumentNodes) return false;
      if (this.isMaterialNode(node) && !this.showMaterialNodes) return false;
      if (this.isRecordNode(node) && !this.showRecordNodes) return false;
      return true;
    },

    isSatelliteNode(node) {
      if (!node) return false;
      if (this.startNode && String(node.id) === String(this.startNode.id)) return false;
      return this.isInstrumentNode(node) || this.isMaterialNode(node) || this.isRecordNode(node);
    },

    firstDefinedValue(values = []) {
      return values.find((value) => value !== undefined && value !== null && value !== "") ?? null;
    },

    getValueByPath(source, path) {
      if (!source || !path) return undefined;
      const segments = Array.isArray(path) ? path : String(path).split(".");
      let current = source;

      for (const segment of segments) {
        if (current === null || current === undefined) return undefined;
        current = current[segment];
      }

      return current;
    },

    firstDefinedFromPaths(source, paths = []) {
      return this.firstDefinedValue(paths.map((path) => this.getValueByPath(source, path)));
    },

    firstNumberFromPaths(source, paths = []) {
      return this.firstFiniteNumber(paths.map((path) => this.getValueByPath(source, path)));
    },

    firstFiniteNumber(values = []) {
      for (const value of values) {
        const parsed = Number(value);
        if (Number.isFinite(parsed)) {
          return parsed;
        }
      }
      return null;
    },

    compareOptionalNumbers(left, right) {
      const leftHasValue = Number.isFinite(left);
      const rightHasValue = Number.isFinite(right);

      if (leftHasValue && rightHasValue) return left - right;
      if (leftHasValue) return -1;
      if (rightHasValue) return 1;
      return 0;
    },

    compareText(left, right) {
      return String(left || "").localeCompare(String(right || ""), undefined, {
        sensitivity: "base",
        numeric: true,
      });
    },

    buildStructureElementContexts(structureRoots = [], projectId = null) {
      const lookup = {};
      let nextOrder = 0;

      const visit = (nodes = [], parentPath = "") => {
        (Array.isArray(nodes) ? nodes : []).forEach((node) => {
          const elementId = Number(node?.id);
          if (!Number.isFinite(elementId)) {
            if (Array.isArray(node?.children) && node.children.length) {
              visit(node.children, parentPath);
            }
            return;
          }

          nextOrder += 1;
          const nodeName = node?.name || `Structure ${elementId}`;
          const path = node?.path || (parentPath ? `${parentPath} / ${nodeName}` : nodeName);

          lookup[elementId] = {
            elementId,
            projectId: Number.isFinite(Number(projectId)) ? Number(projectId) : null,
            name: nodeName,
            path,
            order: nextOrder,
          };

          if (Array.isArray(node?.children) && node.children.length) {
            visit(node.children, path);
          }
        });
      };

      visit(structureRoots);
      return lookup;
    },

    async fetchProjectStructureContexts(nodesInput = []) {
      const projectIds = Array.from(
        new Set(
          (Array.isArray(nodesInput) ? nodesInput : [])
            .map((node) => this.resolveNodeProjectId(node))
            .filter((projectId) => Number.isFinite(projectId))
        )
      );

      if (!projectIds.length) {
        this.structureElementContextById = {};
        return;
      }

      const existingLookup = { ...(this.structureElementContextById || {}) };
      const loadedProjectIds = new Set(
        Object.values(existingLookup)
          .map((entry) => Number(entry?.projectId))
          .filter((projectId) => Number.isFinite(projectId))
      );
      const missingProjectIds = projectIds.filter((projectId) => !loadedProjectIds.has(Number(projectId)));

      if (!missingProjectIds.length) {
        return;
      }

      const hierarchyResponses = await Promise.all(
        missingProjectIds.map(async (projectId) => {
          try {
            const response = await apiService.getProjectHierarchy(projectId, this.loggedInUser?.entra_id);
            return {
              projectId,
              structure: Array.isArray(response?.structure) ? response.structure : [],
            };
          } catch (error) {
            console.warn(`Failed to load structure context for project ${projectId}:`, error);
            return { projectId, structure: [] };
          }
        })
      );

      hierarchyResponses.forEach(({ projectId, structure }) => {
        Object.assign(existingLookup, this.buildStructureElementContexts(structure, projectId));
      });

      this.structureElementContextById = existingLookup;
    },

    resolveStructureElementId(node) {
      return this.firstFiniteNumber([
        this.firstNumberFromPaths(node, STRUCTURE_ELEMENT_ID_PATHS),
      ]);
    },

    resolveStructureElementContext(node, projectId = this.resolveNodeProjectId(node)) {
      const structureElementId = this.resolveStructureElementId(node);
      const fallbackName = this.firstDefinedFromPaths(node, STRUCTURE_ELEMENT_NAME_PATHS);
      const fallbackOrder = this.firstNumberFromPaths(node, STRUCTURE_ELEMENT_ORDER_PATHS);

      if (!Number.isFinite(structureElementId)) {
        return {
          structureElementId: null,
          structureElementName: fallbackName,
          structureElementPath: null,
          structureOrder: fallbackOrder,
        };
      }

      const matchedEntry = this.structureElementContextById?.[structureElementId];
      if (matchedEntry && (projectId == null || matchedEntry.projectId == null || Number(matchedEntry.projectId) === Number(projectId))) {
        return {
          structureElementId,
          structureElementName: matchedEntry.name || fallbackName,
          structureElementPath: matchedEntry.path || null,
          structureOrder: matchedEntry.order ?? fallbackOrder,
        };
      }

      return {
        structureElementId,
        structureElementName: fallbackName,
        structureElementPath: null,
        structureOrder: fallbackOrder,
      };
    },

    extractNodeStoryContext(node) {
      const nodeType = this.normalizeNodeType(node);
      const projectId = this.resolveNodeProjectId(node);
      const projectName = node.project_name
        ?? this.firstDefinedFromPaths(node, ["project.name", "projectName"])
        ?? null;
      const structureContext = this.resolveStructureElementContext(node, projectId);
      const structureElementKey = Number.isFinite(structureContext.structureElementId)
        ? `structure:${structureContext.structureElementId}`
        : null;

      return {
        nodeType,
        projectId,
        projectName,
        structureElementId: structureContext.structureElementId,
        structureElementKey,
        structureElementName: structureContext.structureElementName,
        structureElementPath: structureContext.structureElementPath,
        structureOrder: structureContext.structureOrder,
        explicitStructure: Boolean(structureElementKey),
      };
    },

    buildAdjacencyLookup(nodesInput = this.nodes, edgesInput = this.edges) {
      const nodeIds = new Set((Array.isArray(nodesInput) ? nodesInput : []).map((node) => String(node.id)));

      return (Array.isArray(edgesInput) ? edgesInput : []).reduce((lookup, edge) => {
        if (!nodeIds.has(String(edge.src_node_id)) || !nodeIds.has(String(edge.dst_node_id))) {
          return lookup;
        }

        if (!lookup[edge.src_node_id]) lookup[edge.src_node_id] = new Set();
        if (!lookup[edge.dst_node_id]) lookup[edge.dst_node_id] = new Set();
        lookup[edge.src_node_id].add(edge.dst_node_id);
        lookup[edge.dst_node_id].add(edge.src_node_id);
        return lookup;
      }, {});
    },

    selectDominantStoryContext(contexts, keyField) {
      const explicitField = "explicitStructure";
      const orderField = "structureOrder";
      const nameField = "structureElementName";
      const grouped = new Map();

      (contexts || []).forEach((context) => {
        const key = context?.[keyField];
        if (!key) return;

        const current = grouped.get(key) || {
          count: 0,
          explicitCount: 0,
          sample: context,
        };

        current.count += 1;
        current.explicitCount += context?.[explicitField] ? 1 : 0;

        const currentOrder = current.sample?.[orderField];
        const nextOrder = context?.[orderField];
        if (
          (!Number.isFinite(currentOrder) && Number.isFinite(nextOrder))
          || (Number.isFinite(nextOrder) && Number.isFinite(currentOrder) && nextOrder < currentOrder)
        ) {
          current.sample = context;
        }

        grouped.set(key, current);
      });

      return Array.from(grouped.values())
        .sort((left, right) => {
          if (left.explicitCount !== right.explicitCount) {
            return right.explicitCount - left.explicitCount;
          }
          if (left.count !== right.count) {
            return right.count - left.count;
          }

          const orderCompare = this.compareOptionalNumbers(
            left.sample?.[orderField],
            right.sample?.[orderField]
          );
          if (orderCompare !== 0) return orderCompare;

          return this.compareText(left.sample?.[nameField], right.sample?.[nameField]);
        })[0]?.sample || null;
    },

    buildNodeStoryContexts(nodesInput = this.nodes, edgesInput = this.edges) {
      const nodeList = Array.isArray(nodesInput) ? nodesInput.filter(Boolean) : [];
      const adjacency = this.buildAdjacencyLookup(nodeList, edgesInput);
      const contexts = nodeList.reduce((lookup, node) => {
        lookup[node.id] = this.extractNodeStoryContext(node);
        return lookup;
      }, {});

      for (let pass = 0; pass < 4; pass += 1) {
        let changed = false;

        nodeList.forEach((node) => {
          const context = contexts[node.id];
          const neighborContexts = Array.from(adjacency[node.id] || [])
            .map((neighborId) => contexts[neighborId])
            .filter(Boolean);

          if (!neighborContexts.length) return;

          // Priority 1: inherit project_id (most stable anchor)
          if (context.projectId == null) {
            const projectCounts = new Map();
            neighborContexts.forEach((nc) => {
              if (nc.projectId == null) return;
              const entry = projectCounts.get(nc.projectId) || { count: 0, sample: nc };
              entry.count += 1;
              projectCounts.set(nc.projectId, entry);
            });
            const bestProject = Array.from(projectCounts.values())
              .sort((left, right) => right.count - left.count)[0];
            if (bestProject) {
              context.projectId = bestProject.sample.projectId;
              context.projectName = context.projectName || bestProject.sample.projectName;
              changed = true;
            }
          }

          // Priority 2: inherit structure-element context
          if (!context.structureElementKey) {
            const inheritedStructure = this.selectDominantStoryContext(neighborContexts, "structureElementKey");
            if (inheritedStructure?.structureElementKey) {
              context.structureElementKey = inheritedStructure.structureElementKey;
              context.structureElementId = this.firstFiniteNumber([
                context.structureElementId,
                inheritedStructure.structureElementId,
              ]);
              context.structureElementName = context.structureElementName || inheritedStructure.structureElementName;
              context.structureElementPath = context.structureElementPath || inheritedStructure.structureElementPath;
              context.structureOrder = this.firstFiniteNumber([
                context.structureOrder,
                inheritedStructure.structureOrder,
              ]);
              changed = true;
            }
          }

        });

        if (!changed) break;
      }

      return contexts;
    },

    nodeStoryRank(node, columnKind = "structure") {
      const nodeType = this.normalizeNodeType(node);
      const baseRanks = {
        experiment: 0,
        material: 1,
        location: 2,
        project: 3,
        workflow: 4,
        action: 5,
        record: 3,
        instrument: 6,
        mdlist: 7,
        md_list: 7,
        unknown: 8,
      };

      if (columnKind === "structure") {
        return baseRanks[nodeType] ?? 8;
      }

      return baseRanks[nodeType] ?? 8;
    },

    sortNodesWithinColumn(nodes, contexts = {}, columnKind = "structure") {
      return (Array.isArray(nodes) ? nodes : [])
        .slice()
        .sort((left, right) => {
          const leftContext = contexts[left.id] || {};
          const rightContext = contexts[right.id] || {};

          const rankCompare = this.nodeStoryRank(left, columnKind) - this.nodeStoryRank(right, columnKind);
          if (rankCompare !== 0) return rankCompare;

          const structureOrderCompare = this.compareOptionalNumbers(leftContext.structureOrder, rightContext.structureOrder);
          if (columnKind === "structure" && structureOrderCompare !== 0) return structureOrderCompare;

          if (Boolean(left?.is_start) !== Boolean(right?.is_start)) {
            return left?.is_start ? -1 : 1;
          }

          const typeCompare = this.compareText(leftContext.nodeType, rightContext.nodeType);
          if (typeCompare !== 0) return typeCompare;

          const nameCompare = this.compareText(left?.name, right?.name);
          if (nameCompare !== 0) return nameCompare;

          return this.compareOptionalNumbers(this.resolveNodeEntityId(left), this.resolveNodeEntityId(right));
        });
    },

    // Orders project lanes: start node's project first, then by BFS graph distance,
    // then alphabetically. This ensures the focal project is always the leftmost lane.
    resolveProjectOrder(projectGroups, startProjectKey, nodeList, edgesInput) {
      const keys = Array.from(projectGroups.keys());
      if (keys.length <= 1) return keys;

      const adjacency = this.buildAdjacencyLookup(nodeList, edgesInput);
      const nodeLookupLocal = nodeList.reduce((acc, n) => {
        acc[String(n.id)] = n;
        return acc;
      }, {});

      // BFS from start node to find minimum graph distance to each project
      const projectMinDist = new Map();
      if (this.startNode) {
        const startPk = this.startNode.project_id != null
          ? `project:${this.startNode.project_id}`
          : null;
        if (startPk) projectMinDist.set(startPk, 0);

        const queue = [{ id: this.startNode.id, dist: 0 }];
        const visited = new Set([String(this.startNode.id)]);
        let qi = 0;

        while (qi < queue.length) {
          const { id, dist } = queue[qi];
          qi += 1;

          Array.from(adjacency[id] || []).forEach((neighborId) => {
            const nidStr = String(neighborId);
            if (visited.has(nidStr)) return;
            visited.add(nidStr);
            const neighborNode = nodeLookupLocal[nidStr];
            if (neighborNode?.project_id != null) {
              const pk = `project:${neighborNode.project_id}`;
              if (!projectMinDist.has(pk)) projectMinDist.set(pk, dist + 1);
            }
            queue.push({ id: neighborId, dist: dist + 1 });
          });
        }
      }

      return keys.slice().sort((a, b) => {
        if (a === startProjectKey) return -1;
        if (b === startProjectKey) return 1;
        const distA = projectMinDist.get(a) ?? Infinity;
        const distB = projectMinDist.get(b) ?? Infinity;
        if (distA !== distB) return distA - distB;
        return this.compareText(
          projectGroups.get(a)?.projectName,
          projectGroups.get(b)?.projectName
        );
      });
    },

    // Builds columns using project lanes → ordered structure-element columns.
    // Nodes without any structure-element context fall back to level buckets.
    buildStoryColumns(nodesInput = this.nodes, edgesInput = this.edges) {
      const nodeList = Array.isArray(nodesInput) ? nodesInput.filter(Boolean) : [];
      const contexts = this.buildNodeStoryContexts(nodeList, edgesInput);

      // 1. Group by project → structure element.
      const projectGroups = new Map();

      nodeList.forEach((node) => {
        const context = contexts[node.id] || {};
        const projectId = node.project_id ?? context.projectId ?? null;
        const projectName = node.project_name
          ?? context.projectName
          ?? (projectId != null ? `Project ${projectId}` : null);
        const projectKey = projectId != null ? `project:${projectId}` : "project:unknown";

        const structureElementId = context.structureElementId ?? null;
        const structureElementKey = context.structureElementKey ?? null;
        const structureOrder = context.structureOrder ?? null;
        const structureElementName = context.structureElementName ?? null;
        const groupKey = structureElementKey ? `${projectKey}::${structureElementKey}` : null;

        if (!groupKey) return;

        if (!projectGroups.has(projectKey)) {
          projectGroups.set(projectKey, {
            projectKey,
            projectId,
            projectName,
            groups: new Map(),
          });
        }

        const project = projectGroups.get(projectKey);
        if (!project.groups.has(groupKey)) {
          project.groups.set(groupKey, {
            key: groupKey,
            structureElementId: Number.isFinite(structureElementId) ? structureElementId : null,
            structureOrder,
            structureElementName,
            nodes: [],
          });
        }

        const group = project.groups.get(groupKey);
        group.structureOrder = this.firstFiniteNumber([group.structureOrder, structureOrder]);
        group.nodes.push(node);
      });

      if (!projectGroups.size) return null;

      // 2. Order project lanes
      const startProjectKey = this.startNode?.project_id != null
        ? `project:${this.startNode.project_id}`
        : null;
      const projectOrder = this.resolveProjectOrder(
        projectGroups, startProjectKey, nodeList, edgesInput
      );

      // 3. Build columns — one column per structure element, with a lane gap between projects.
      const columns = [];
      let isFirstProject = true;

      projectOrder.forEach((projectKey) => {
        const project = projectGroups.get(projectKey);
        if (!project) return;

        const sortedGroups = Array.from(project.groups.values()).sort((left, right) => {
          const structureCmp = this.compareOptionalNumbers(left.structureOrder, right.structureOrder);
          if (structureCmp !== 0) return structureCmp;

          const structureIdCmp = this.compareOptionalNumbers(left.structureElementId, right.structureElementId);
          if (structureIdCmp !== 0) return structureIdCmp;

          const structureNameCmp = this.compareText(left.structureElementName, right.structureElementName);
          if (structureNameCmp !== 0) return structureNameCmp;

          return this.compareText(left.key, right.key);
        });

        sortedGroups.forEach((group, groupIndex) => {
          columns.push({
            kind: "structure",
            key: group.key,
            projectKey,
            projectId: project.projectId,
            projectName: project.projectName,
            structureElementId: group.structureElementId,
            structureOrder: group.structureOrder,
            structureElementName: group.structureElementName,
            // First column of every non-first project gets a visual gap
            breakBefore: !isFirstProject && groupIndex === 0,
            nodes: this.sortNodesWithinColumn(group.nodes, contexts, "structure"),
          });
        });

        isFirstProject = false;
      });

      // 4. Fallback bucket for nodes with no project/structure context
      const placedIds = new Set(
        columns.flatMap((column) => column.nodes.map((node) => String(node.id)))
      );

      const remainingNodes = nodeList.filter((node) => !placedIds.has(String(node.id)));
      if (remainingNodes.length > 0) {
        const fallbackBuckets = this.buildLevelBuckets(remainingNodes, edgesInput);
        fallbackBuckets.forEach((bucket, index) => {
          columns.push({
            kind: "fallback",
            key: `fallback:${index}`,
            breakBefore: columns.length > 0 && index === 0,
            nodes: this.sortNodesWithinColumn(bucket, contexts, "fallback"),
          });
        });
      }

      return columns.filter((column) => Array.isArray(column.nodes) && column.nodes.length > 0);
    },

    satelliteAnchorRank(node) {
      const nodeType = this.normalizeNodeType(node);
      const ranks = {
        experiment: 0,
        action: 1,
        workflow: 2,
        material: 3,
        project: 4,
        location: 5,
        instrument: 6,
        record: 7,
        unknown: 8,
      };

      return ranks[nodeType] ?? 8;
    },

    resolveSatelliteAnchor(node, positions, adjacency) {
      const neighborIds = Array.from(adjacency[node.id] || []);
      const candidates = neighborIds
        .map((neighborId) => this.nodeLookup[neighborId])
        .filter((candidate) => candidate && positions[candidate.id] && !this.isSatelliteNode(candidate));

      if (!candidates.length) {
        return this.startNode && positions[this.startNode.id] ? this.startNode : null;
      }

      return candidates
        .slice()
        .sort((left, right) => {
          const rankCompare = this.satelliteAnchorRank(left) - this.satelliteAnchorRank(right);
          if (rankCompare !== 0) return rankCompare;

          const leftPos = positions[left.id];
          const rightPos = positions[right.id];
          const leftX = leftPos?.x ?? 0;
          const rightX = rightPos?.x ?? 0;
          if (leftX !== rightX) return leftX - rightX;

          return this.compareText(left?.name, right?.name);
        })[0] || null;
    },

    applySatelliteNodePositions(basePositions) {
      const positions = { ...(basePositions || {}) };
      const adjacency = this.buildAdjacencyLookup(this.nodes, this.edges);
      const satelliteGroups = new Map();
      const unresolvedNodes = [];

      this.nodes
        .filter((node) => this.isSatelliteNode(node))
        .forEach((node) => {
          const anchor = this.resolveSatelliteAnchor(node, positions, adjacency);
          if (!anchor || !positions[anchor.id]) {
            unresolvedNodes.push(node);
            return;
          }

          const side = this.isInstrumentNode(node) ? "left" : "right";
          const groupKey = `${anchor.id}:${side}`;
          if (!satelliteGroups.has(groupKey)) {
            satelliteGroups.set(groupKey, {
              anchor,
              side,
              nodes: [],
            });
          }
          satelliteGroups.get(groupKey).nodes.push(node);
        });

      satelliteGroups.forEach((group) => {
        const anchorPos = positions[group.anchor.id];
        if (!anchorPos) return;

        const sortedNodes = group.nodes
          .slice()
          .sort((left, right) => this.compareText(left?.name, right?.name));

        const xOffset = group.side === "left" ? -112 : 96;
        const yBaseOffset = 146;
        const ySpacing = 96;
        const startY = anchorPos.y - yBaseOffset - ((sortedNodes.length - 1) * ySpacing);

        sortedNodes.forEach((node, index) => {
          positions[node.id] = {
            x: anchorPos.x + xOffset,
            y: startY + index * ySpacing,
          };
        });
      });

      if (unresolvedNodes.length > 0) {
        const placedEntries = Object.values(positions);
        const fallbackX = placedEntries.length
          ? Math.max(...placedEntries.map((position) => position.x)) + 180
          : this.canvasWidth / 2;
        const fallbackStartY = placedEntries.length
          ? Math.max(DEFAULT_MARGIN_Y, Math.min(...placedEntries.map((position) => position.y)))
          : this.canvasHeight / 2;

        unresolvedNodes
          .slice()
          .sort((left, right) => this.compareText(left?.name, right?.name))
          .forEach((node, index) => {
            positions[node.id] = {
              x: fallbackX,
              y: fallbackStartY + index * 96,
            };
          });
      }

      return positions;
    },

    expandCanvasToFitPositions(basePositions, padding = 96) {
      const positionEntries = Object.entries(basePositions || {});
      if (!positionEntries.length) {
        return { positions: basePositions || {}, width: this.canvasWidth, height: this.canvasHeight };
      }

      const xs = positionEntries.map(([, position]) => position.x);
      const ys = positionEntries.map(([, position]) => position.y);
      const minX = Math.min(...xs);
      const maxX = Math.max(...xs);
      const minY = Math.min(...ys);
      const maxY = Math.max(...ys);
      const shiftX = minX < padding ? padding - minX : 0;
      const shiftY = minY < padding ? padding - minY : 0;

      const nextPositions = positionEntries.reduce((lookup, [nodeId, position]) => {
        lookup[nodeId] = {
          x: position.x + shiftX,
          y: position.y + shiftY,
        };
        return lookup;
      }, {});

      const nextWidth = this.clamp(
        Math.ceil(maxX + shiftX + padding),
        1100,
        MAX_AUTO_CANVAS_WIDTH
      );
      const nextHeight = this.clamp(
        Math.ceil(maxY + shiftY + padding),
        620,
        MAX_AUTO_CANVAS_HEIGHT
      );

      return {
        positions: nextPositions,
        width: nextWidth,
        height: nextHeight,
      };
    },

    handleNodeVisibilityToggle() {
      this.hideTooltip();

      if (this.nodePopup.visible && this.nodePopup.node && !this.shouldDisplayNode(this.nodePopup.node)) {
        this.closeNodePopup();
      }

      if (this.edgePopup.visible && this.edgePopup.edge) {
        const edgeStillVisible = this.edges.some((edge) => String(edge.id) === String(this.edgePopup.edge?.id));
        if (!edgeStillVisible) {
          this.closeEdgePopup();
        }
      }

      this.resetLayout();
    },

    resetLayout() {
      if (!this.nodes.length) {
        this.positions = {};
        return;
      }

      const primaryNodes = this.nodes.filter((node) => !this.isSatelliteNode(node));
      const layoutNodes = primaryNodes.length > 0 ? primaryNodes : this.nodes;
      const storyColumns = this.buildStoryColumns(layoutNodes, this.edges);
      const columns = (storyColumns && storyColumns.length > 0)
        ? storyColumns
        : this.buildLevelBuckets(layoutNodes, this.edges).map((bucket, index) => ({
            kind: "level",
            key: `level:${index}`,
            breakBefore: false,
            nodes: this.sortNodesWithinColumn(bucket, {}, "fallback"),
          }));

      const columnCount = Math.max(columns.length, 1);
      const maxBucketSize = columns.reduce((max, column) => Math.max(max, column.nodes.length), 1);
      const workflowBreakCount = columns.reduce(
        (count, column, index) => count + (index > 0 && column.breakBefore ? 1 : 0),
        0
      );

      const horizontalSpacing = this.nodes.length > 180 ? 170 : this.nodes.length > 100 ? 200 : 240;
      const verticalSpacing = this.nodes.length > 180 ? 110 : this.nodes.length > 100 ? 130 : 150;
      const workflowGap = Math.round(horizontalSpacing * 0.45);

      let nextCanvasWidth = this.clamp(
        DEFAULT_MARGIN_X * 2
          + Math.max(columnCount - 1, 0) * horizontalSpacing
          + workflowBreakCount * workflowGap
          + 120,
        1100,
        MAX_AUTO_CANVAS_WIDTH
      );
      let nextCanvasHeight = this.clamp(
        DEFAULT_MARGIN_Y * 2
          + Math.max(maxBucketSize - 1, 0) * verticalSpacing
          + 140,
        620,
        MAX_AUTO_CANVAS_HEIGHT
      );

      this.canvasWidth = nextCanvasWidth;
      this.canvasHeight = nextCanvasHeight;

      const positions = {};
      let currentX = DEFAULT_MARGIN_X;

      columns.forEach((column, columnIndex) => {
        if (columnIndex > 0) {
          currentX += horizontalSpacing;
          if (column.breakBefore) {
            currentX += workflowGap;
          }
        }

        column.nodes.forEach((node, nodeIndex) => {
          positions[node.id] = {
            x: columnCount === 1 ? this.canvasWidth / 2 : currentX,
            y: column.nodes.length === 1 && column.kind === "level"
              ? this.canvasHeight / 2
              : DEFAULT_MARGIN_Y + nodeIndex * verticalSpacing,
          };
        });
      });

      const withSatellitePositions = this.applySatelliteNodePositions(positions);
      const fittedLayout = this.expandCanvasToFitPositions(withSatellitePositions, 110);

      nextCanvasWidth = fittedLayout.width;
      nextCanvasHeight = fittedLayout.height;
      this.canvasWidth = nextCanvasWidth;
      this.canvasHeight = nextCanvasHeight;
      this.positions = fittedLayout.positions;
      this.resetView();
    },

    resetView() {
      this.zoomBaselineWidth = this.canvasWidth;
      this.zoomBaselineHeight = this.canvasHeight;

      if (!this.hasGraph) {
        this.zoom = 1;
        this.panX = 0;
        this.panY = 0;
        return;
      }

      this.$nextTick(() => {
        this.fitAllNodesToView();
      });
    },

    fitAllNodesToView() {
      const posEntries = Object.values(this.positions || {});
      if (!posEntries.length) return;

      const svg = this.$refs.svgRef;
      const rect = svg ? svg.getBoundingClientRect() : null;

      const maxRadius = 60;
      const xs = posEntries.map((p) => p.x);
      const ys = posEntries.map((p) => p.y);
      const minX = Math.min(...xs) - maxRadius;
      const maxX = Math.max(...xs) + maxRadius;
      const minY = Math.min(...ys) - maxRadius;
      const maxY = Math.max(...ys) + maxRadius;
      const contentW = maxX - minX;
      const contentH = maxY - minY;
      if (!contentW || !contentH) return;

      let visW = this.canvasWidth;
      let visH = this.canvasHeight;

      if (rect && rect.width && rect.height) {
        const pxPerUnit = Math.min(rect.width / this.canvasWidth, rect.height / this.canvasHeight);
        visW = rect.width / pxPerUnit;
        visH = rect.height / pxPerUnit;
      }

      const padding = 80;
      const fitZoom = this.clamp(
        Math.min((visW - padding * 2) / contentW, (visH - padding * 2) / contentH),
        MIN_ZOOM,
        MAX_ZOOM
      );

      const centerX = (minX + maxX) / 2;
      const centerY = (minY + maxY) / 2;

      this.zoomBaselineWidth = this.canvasWidth;
      this.zoomBaselineHeight = this.canvasHeight;
      this.zoom = fitZoom;
      this.panX = this.canvasWidth / 2 - centerX * fitZoom;
      this.panY = this.canvasHeight / 2 - centerY * fitZoom;
    },

    buildLevelBuckets(nodesInput = this.nodes, edgesInput = this.edges) {
      const nodeList = Array.isArray(nodesInput) ? nodesInput.filter(Boolean) : [];
      if (!nodeList.length) return [];
      if (!this.startNode) return [nodeList];

      const adjacency = this.buildAdjacencyLookup(nodeList, edgesInput);

      const queue = [this.startNode.id];
      const distances = { [this.startNode.id]: 0 };
      let index = 0;

      while (index < queue.length) {
        const currentId = queue[index];
        index += 1;

        const neighbors = adjacency[currentId] ? Array.from(adjacency[currentId]) : [];
        neighbors.forEach((neighborId) => {
          if (distances[neighborId] !== undefined) return;
          distances[neighborId] = distances[currentId] + 1;
          queue.push(neighborId);
        });
      }

      const bucketMap = {};
      const overflowBucket = [];

      nodeList.forEach((node) => {
        const level = distances[node.id];
        if (level === undefined) {
          overflowBucket.push(node);
          return;
        }

        if (!bucketMap[level]) bucketMap[level] = [];
        bucketMap[level].push(node);
      });

      const orderedBuckets = Object.keys(bucketMap)
        .map((key) => Number(key))
        .sort((left, right) => left - right)
        .map((level) => bucketMap[level]);

      if (overflowBucket.length > 0) {
        orderedBuckets.push(overflowBucket);
      }

      return orderedBuckets.length > 0 ? orderedBuckets : [nodeList];
    },

    nodePosition(nodeId) {
      return this.positions[nodeId] || {
        x: this.canvasWidth / 2,
        y: this.canvasHeight / 2,
      };
    },

    hasEdgePositions(edge) {
      return Boolean(this.positions[edge.src_node_id] && this.positions[edge.dst_node_id]);
    },

    edgeMidpoint(edge) {
      const source = this.nodePosition(edge.src_node_id);
      const target = this.nodePosition(edge.dst_node_id);

      return {
        x: (source.x + target.x) / 2,
        y: (source.y + target.y) / 2,
      };
    },

    edgeLabelAngle(edge) {
      const source = this.nodePosition(edge.src_node_id);
      const target = this.nodePosition(edge.dst_node_id);

      const radians = Math.atan2(target.y - source.y, target.x - source.x);
      let degrees = (radians * 180) / Math.PI;

      if (degrees > 90 || degrees < -90) {
        degrees += 180;
      }

      return degrees;
    },

    edgeLabelTransform(edge) {
      const midpoint = this.edgeMidpoint(edge);
      return `rotate(${this.edgeLabelAngle(edge)} ${midpoint.x} ${midpoint.y - 10})`;
    },

    nodeRadius(node) {
      return this.startNode && node.id === this.startNode.id ? 58 : 50;
    },

    nodeCalibrationRingRadius(node) {
      return Math.max(this.nodeRadius(node) - 7, 10);
    },

    resolveInstrumentNextCalibration(node) {
      return this.firstDefinedFromPaths(node, [
        "n_calibration_time",
        "next_calibration_time",
        "next_calibration_date",
        "calibration_due_date",
      ]);
    },

    instrumentCalibrationRingState(node) {
      if (!this.isInstrumentNode(node)) return null;

      const nextCalibration = this.resolveInstrumentNextCalibration(node);
      if (!nextCalibration) return "not-available";

      const dueTime = new Date(nextCalibration).getTime();
      if (!Number.isFinite(dueTime)) return "not-available";

      return dueTime >= Date.now() ? "within-window" : "overdue";
    },

    showInstrumentCalibrationRing(node) {
      return Boolean(this.instrumentCalibrationRingState(node));
    },

    nodeCalibrationRingClass(node) {
      const state = this.instrumentCalibrationRingState(node);
      return state ? `is-${state}` : "";
    },

    truncate(value, maxLength = 14) {
      if (!value) return "—";
      return value.length > maxLength ? `${value.slice(0, maxLength - 1)}…` : value;
    },

    resolveNodeWorkflowLabel(node) {
      return node?.workflow_name
        || (typeof node?.workflow === "object" ? node?.workflow?.name : node?.workflow)
        || null;
    },

    resolveNodeActivityLabel(node) {                                                                                                                                                                                                                                                                                                                                                                                                           
      return node?.activity_name
        || node?.action_name
        || (typeof node?.activity === "object" ? node?.activity?.name : node?.activity)
        || null;
    },

    nodeSecondaryLabel(node) {
      const nodeType = this.normalizeNodeType(node);                                                                                                                                                                                                                                                                        
      if (nodeType === "experiment") {
        return this.resolveNodeWorkflowLabel(node)
          || this.resolveNodeActivityLabel(node)
          || null;
      }
      return node?.node_type || null;
    },

    nodeTertiaryLabel(node) {
      const nodeType = this.normalizeNodeType(node);
      if (nodeType !== "experiment") return null;

      const workflowLabel = this.resolveNodeWorkflowLabel(node);
      const activityLabel = this.resolveNodeActivityLabel(node);

      if (!workflowLabel || !activityLabel) return null;
      if (String(workflowLabel).trim().toLowerCase() === String(activityLabel).trim().toLowerCase()) {
        return null;
      }

      return activityLabel;
    },

    buildNodeDetailsPayload(nodeInput) {
      const node = nodeInput || {};
      const isRecordNode = String(node.node_type || "").toLowerCase() === "record";
      const isExperimentNode = String(node.node_type || "").toLowerCase() === "experiment";
      const workflowName = node.workflow_name
        || (typeof node.workflow === "object" ? node.workflow?.name : node.workflow)
        || null;
      const activityName = node.activity_name
        || node.action_name
        || (typeof node.activity === "object" ? node.activity?.name : node.activity)
        || null;

      const nodeDetails = {
        ...node,
      };

      if (isRecordNode) {
        nodeDetails.record_type = node.record_type_name || node.record_type || null;
      } else {
        delete nodeDetails.record_type;
      }

      if (isExperimentNode) {
        delete nodeDetails.action_order;
        delete nodeDetails.activity_order;
      }

      delete nodeDetails.name;
      delete nodeDetails.record_type_name;

      if (workflowName) {
        nodeDetails.workflow_name = workflowName;
      }
      if (activityName) {
        nodeDetails.activity_name = activityName;
      }

      return nodeDetails;
    },

    resolveNodeAssType(node) {
      const normalized = String(node?.node_type || "").toLowerCase();
      if (["experiment", "record", "material", "instrument", "workflow", "location", "project"].includes(normalized)) {
        return normalized;
      }
      return null;
    },

    resolveMdListId(mdListEntry) {
      if (!mdListEntry || typeof mdListEntry !== "object") return null;
      const rawId = mdListEntry.metadata_list_id ?? mdListEntry.md_list_id ?? mdListEntry.mdlist_id ?? mdListEntry.mdListId ?? mdListEntry.id;
      const parsed = Number(rawId);
      return Number.isFinite(parsed) ? parsed : null;
    },

    async fetchFallbackMdListBindings(assType, entityId) {
      if (assType === "instrument") {
        const instrument = await apiService.get(API_ENDPOINTS.instrument(entityId), {}, this.loggedInUser?.entra_id);
        const mdListId = Number(instrument?.metadata_list_id);
        return Number.isFinite(mdListId) ? [{ metadata_list_id: mdListId }] : [];
      }

      if (assType === "material") {
        const material = await apiService.get(API_ENDPOINTS.material(entityId), {}, this.loggedInUser?.entra_id);
        const mdListId = Number(material?.metadata_list_id);
        return Number.isFinite(mdListId) ? [{ metadata_list_id: mdListId }] : [];
      }

      return [];
    },

    async loadMdListDetails(mdListId, assType, assId) {
      const entraId = this.loggedInUser?.entra_id;

      const [mdList, keyValues] = await Promise.all([
        apiService.get(API_ENDPOINTS.mdlist(mdListId), { entity_type: assType }, entraId),
        apiService.get(
          API_ENDPOINTS.keyValues(mdListId),
          buildMetadataEntityParams(assType, assId),
          entraId
        ).catch(() => []),
      ]);

      return {
        mdListId,
        mdList,
        keyValues: normalizeMetadataKeyValueCollection(keyValues),
      };
    },

    async openNodePopup(node) {
      const metadataAssId = this.resolveNodeMetadataId(node);
      const entityId = this.resolveNodeEntityId(node);
      const assType = this.resolveNodeAssType(node);

      this.nodePopup = {
        visible: true,
        loading: true,
        error: "",
        node,
        mdLists: [],
        showAddConnection: false,
      };

      if (!Number.isFinite(metadataAssId) || !assType) {
        this.nodePopup.loading = false;
        this.nodePopup.error = "This node type is not supported for metadata lookup.";
        return;
      }

      try {
        let bindingArray = [];
        try {
          const mdListBindings = await listEntityMdLists(assType, metadataAssId, this.loggedInUser?.entra_id);
          bindingArray = Array.isArray(mdListBindings) ? mdListBindings : [];
        } catch (error) {
          const status = error?.response?.status;
          try {
            bindingArray = await this.fetchFallbackMdListBindings(assType, entityId);
          } catch (fallbackError) {
            if (status !== 404) {
              throw error;
            }
            bindingArray = [];
          }
        }

        const mdListsWithDetails = await Promise.all(bindingArray.map(async (entry) => {
          const mdListId = this.resolveMdListId(entry);
          if (!mdListId) return null;

          try {
            return await this.loadMdListDetails(mdListId, assType, metadataAssId);
          } catch (error) {
            console.error(`Failed to load metadata list ${mdListId}`, error);
            return {
              mdListId,
              mdList: entry,
              keyValues: [],
            };
          }
        }));

        this.nodePopup.mdLists = mdListsWithDetails.filter(Boolean);
      } catch (error) {
        console.error("Failed to load node metadata details", error);
        this.nodePopup.error = "Failed to load metadata lists for this node.";
      } finally {
        this.nodePopup.loading = false;
      }
    },

    closeNodePopup() {
      this.nodePopup.visible = false;
      this.nodePopup.showAddConnection = false;
    },

    buildEdgeDetailsPayload(edgeInput) {
      const edge = edgeInput || {};
      const sourceNode = this.nodeLookup[edge.src_node_id];
      const targetNode = this.nodeLookup[edge.dst_node_id];

      return {
        ...edge,
        source_node_name: sourceNode?.name || "Unknown",
        source_node_type: sourceNode?.node_type || sourceNode?.kind || "Unknown",
        target_node_name: targetNode?.name || "Unknown",
        target_node_type: targetNode?.node_type || targetNode?.kind || "Unknown",
      };
    },

    async ensureEdgeRelationTypesLoaded() {
      if (Array.isArray(this.edgePopup.relationTypes) && this.edgePopup.relationTypes.length > 0) {
        return;
      }

      this.edgePopup.loadingRelationTypes = true;
      try {
        const response = await axios.get(API_ENDPOINTS.graphRelationTypes);
        this.edgePopup.relationTypes = Array.isArray(response.data) ? response.data : [];
      } catch (error) {
        console.error("Failed to load relation types", error);
        this.edgePopup.error = "Failed to load relation types.";
      } finally {
        this.edgePopup.loadingRelationTypes = false;
      }
    },

    async openEdgePopup(edge) {
      this.hideTooltip();
      this.closeNodePopup();

      this.edgePopup = {
        ...this.edgePopup,
        visible: true,
        edge,
        selectedRelationTypeId: Number(edge?.relation_type_id) || null,
        creatingRelationType: false,
        showCreateRelationForm: false,
        newRelationTypeName: "",
        newRelationTypeCategory: "",
        saving: false,
        error: "",
      };

      await this.ensureEdgeRelationTypesLoaded();
    },

    closeEdgePopup() {
      this.edgePopup.visible = false;
      this.edgePopup.edge = null;
      this.edgePopup.selectedRelationTypeId = null;
      this.edgePopup.creatingRelationType = false;
      this.edgePopup.showCreateRelationForm = false;
      this.edgePopup.newRelationTypeName = "";
      this.edgePopup.newRelationTypeCategory = "";
      this.edgePopup.error = "";
      this.edgePopup.saving = false;
    },

    handleEdgeClick(edge) {
      this.openEdgePopup(edge);
    },

    toggleCreateEdgeRelationForm() {
      const next = !this.edgePopup.showCreateRelationForm;
      this.edgePopup.showCreateRelationForm = next;
      this.edgePopup.error = "";
      if (!next) {
        this.edgePopup.newRelationTypeName = "";
        this.edgePopup.newRelationTypeCategory = "";
      }
    },

    async createEdgeRelationType() {
      const name = this.edgePopup.newRelationTypeName.trim();
      const category = this.edgePopup.newRelationTypeCategory.trim();
      if (!name) return;

      this.edgePopup.creatingRelationType = true;
      this.edgePopup.error = "";

      try {
        const response = await axios.post(API_ENDPOINTS.graphRelationTypes, {
          name,
          category: category || null,
        });

        const created = response?.data;
        if (!created || !created.id) {
          throw new Error("Invalid relation type response");
        }

        this.edgePopup.relationTypes = [
          ...this.edgePopup.relationTypes,
          created,
        ];
        this.edgePopup.selectedRelationTypeId = Number(created.id);
        this.edgePopup.showCreateRelationForm = false;
        this.edgePopup.newRelationTypeName = "";
        this.edgePopup.newRelationTypeCategory = "";
      } catch (error) {
        console.error("Failed to create relation type", error);
        this.edgePopup.error = "Failed to create relation type.";
      } finally {
        this.edgePopup.creatingRelationType = false;
      }
    },

    async saveEdgeRelationType() {
      if (!this.canSaveEdgeRelation) return;

      const edgeId = Number(this.edgePopup.edge?.id);
      const srcNodeId = Number(this.edgePopup.edge?.src_node_id);
      const dstNodeId = Number(this.edgePopup.edge?.dst_node_id);
      const oldRelationTypeId = Number(this.edgePopup.edge?.relation_type_id);
      const selectedRelationTypeId = Number(this.edgePopup.selectedRelationTypeId);
      const entraId = this.loggedInUser?.entra_id;

      if (!Number.isFinite(edgeId) || !Number.isFinite(srcNodeId) || !Number.isFinite(dstNodeId)) {
        this.edgePopup.error = "This edge cannot be updated.";
        return;
      }
      if (!Number.isFinite(selectedRelationTypeId)) {
        this.edgePopup.error = "Select a valid relation type.";
        return;
      }
      if (!entraId) {
        this.edgePopup.error = "Missing user context (entra_id).";
        return;
      }

      this.edgePopup.saving = true;
      this.edgePopup.error = "";

      try {
        await axios.delete(API_ENDPOINTS.graphEdges, {
          data: {
            entra_id: entraId,
            id: edgeId,
          },
        });

        try {
          await axios.post(API_ENDPOINTS.graphEdges, {
            entra_id: entraId,
            src_node_id: srcNodeId,
            dst_node_id: dstNodeId,
            relation_type_id: selectedRelationTypeId,
          });
        } catch (createError) {
          if (Number.isFinite(oldRelationTypeId)) {
            try {
              await axios.post(API_ENDPOINTS.graphEdges, {
                entra_id: entraId,
                src_node_id: srcNodeId,
                dst_node_id: dstNodeId,
                relation_type_id: oldRelationTypeId,
              });
            } catch (restoreError) {
              console.error("Failed to restore original edge after update failure", restoreError);
            }
          }
          throw createError;
        }

        await this.fetchGraph();

        const refreshedEdge = this.edges.find((item) => (
          Number(item.src_node_id) === srcNodeId
          && Number(item.dst_node_id) === dstNodeId
          && Number(item.relation_type_id) === selectedRelationTypeId
        )) || null;

        this.edgePopup.edge = refreshedEdge;
        this.edgePopup.selectedRelationTypeId = Number(refreshedEdge?.relation_type_id) || selectedRelationTypeId;
      } catch (error) {
        console.error("Failed to update edge relation type", error);
        this.edgePopup.error = "Failed to update relation type.";
      } finally {
        this.edgePopup.saving = false;
      }
    },

    openAddConnectionPanel() {
      if (!this.nodePopupEntityId) return;
      this.nodePopup.showAddConnection = true;
    },

    closeAddConnectionPanel() {
      this.nodePopup.showAddConnection = false;
    },

    nodeDetailRoute(node) {
      const nodeId = this.resolveNodeEntityId(node);
      if (!Number.isFinite(nodeId)) return null;

      const rawType = String(node?.node_type || node?.kind || "").toLowerCase().trim();
      const normalizedType = rawType
        .replace(/[^a-z_]/g, "")
        .replace(/s$/, "");

      const routeByType = {
        experiment: "ExperimentDetailPage",
        record: "RecordDetailPage",
        material: "MaterialDetailPage",
        workflow: "WorkflowDetailPage",
        instrument: "InstrumentDetailPage",
        location: "LocationDetailPage",
        project: "ProjectDetailPage",
        action: "ActionDetailPage",
        mdlist: "MdListDetailPage",
        md_list: "MdListDetailPage",
      };

      const routeName = routeByType[normalizedType] || routeByType[rawType];
      if (!routeName) return null;

      return {
        name: routeName,
        params: { id: String(nodeId) },
      };
    },

    async goToNodePage() {
      const route = this.nodeDetailRoute(this.nodePopup.node);
      if (!route) return;

      this.closeNodePopup();
      try {
        await this.$router.push(route);
      } catch (error) {
        const resolved = this.$router.resolve(route);
        if (resolved?.href) {
          window.location.assign(resolved.href);
        }
      }
    },

    async handleNodeConnectionCreated() {
      this.nodePopup.showAddConnection = false;
      await this.fetchGraph();

      const currentNodeId = this.nodePopup.node?.id;
      if (currentNodeId === undefined || currentNodeId === null || currentNodeId === "") return;

      const refreshedNode = this.nodeLookup[currentNodeId] || this.nodePopup.node;
      await this.openNodePopup(refreshedNode);
    },

    handleNodeClick(node) {
      if (this.suppressNodeClick) {
        this.suppressNodeClick = false;
        return;
      }

      this.openNodePopup(node);
    },

    startDrag(nodeId, event) {
      if (event.button !== 0) return;

      const current = this.nodePosition(nodeId);
      const svgMetrics = this.getSvgMetrics();
      if (!svgMetrics) return;

      this.dragState = {
        nodeId,
        originNodeX: current.x,
        originNodeY: current.y,
        svgScale: svgMetrics.scale,
        startClientX: event.clientX,
        startClientY: event.clientY,
        moved: false,
      };
      this.hideTooltip();
    },

    startPanDrag(event) {
      if (event.button !== 0 || !this.hasGraph || this.dragState) return;

      const svgMetrics = this.getSvgMetrics();
      if (!svgMetrics) return;

      this.panDragState = {
        startClientX: event.clientX,
        startClientY: event.clientY,
        svgScale: svgMetrics.scale,
        originPanX: this.panX,
        originPanY: this.panY,
      };
      this.hideTooltip();
    },

    handleGlobalMouseMove(event) {
      if (this.dragState) {
        const deltaX = Math.abs(event.clientX - this.dragState.startClientX);
        const deltaY = Math.abs(event.clientY - this.dragState.startClientY);
        if (deltaX > 3 || deltaY > 3) {
          this.dragState.moved = true;
        }

        const svgMetrics = this.getSvgMetrics();
        const scale = svgMetrics?.scale || this.dragState.svgScale || 1;

        const deltaClientX = event.clientX - this.dragState.startClientX;
        const deltaClientY = event.clientY - this.dragState.startClientY;
        const desiredX = this.dragState.originNodeX + (deltaClientX * scale) / this.zoom;
        const desiredY = this.dragState.originNodeY + (deltaClientY * scale) / this.zoom;
        const expandedPoint = this.ensureCanvasForPoint(desiredX, desiredY, 82);
        if (expandedPoint.shiftX || expandedPoint.shiftY) {
          this.dragState.originNodeX += expandedPoint.shiftX;
          this.dragState.originNodeY += expandedPoint.shiftY;
        }
        this.pendingDragPosition = {
          nodeId: this.dragState.nodeId,
          x: expandedPoint.x,
          y: expandedPoint.y,
        };
        this.scheduleInteractionFrame();
        return;
      }

      if (!this.panDragState) return;

      const svgMetrics = this.getSvgMetrics();
      const scale = svgMetrics?.scale || this.panDragState.svgScale || 1;

      const deltaClientX = event.clientX - this.panDragState.startClientX;
      const deltaClientY = event.clientY - this.panDragState.startClientY;
      this.pendingPanPosition = {
        panX: this.panDragState.originPanX + deltaClientX * scale,
        panY: this.panDragState.originPanY + deltaClientY * scale,
      };
      this.scheduleInteractionFrame();
    },

    scheduleInteractionFrame() {
      if (this.interactionFrameId) return;

      this.interactionFrameId = requestAnimationFrame(() => {
        if (this.pendingDragPosition) {
          const { nodeId, x, y } = this.pendingDragPosition;
          this.positions = {
            ...this.positions,
            [nodeId]: { x, y },
          };
          this.pendingDragPosition = null;
        }

        if (this.pendingPanPosition) {
          this.panX = this.pendingPanPosition.panX;
          this.panY = this.pendingPanPosition.panY;
          this.pendingPanPosition = null;
        }

        this.interactionFrameId = null;
      });
    },

    constrainToNearestNodeDistance(nodeId, pointX, pointY, maxDistance) {
      const entries = Object.entries(this.positions || {})
        .filter(([id]) => String(id) !== String(nodeId))
        .map(([id, pos]) => ({
          id,
          x: pos?.x ?? 0,
          y: pos?.y ?? 0,
        }));

      if (!entries.length || !Number.isFinite(maxDistance) || maxDistance <= 0) {
        return { x: pointX, y: pointY };
      }

      let nearest = entries[0];
      let nearestDistanceSquared = (pointX - nearest.x) ** 2 + (pointY - nearest.y) ** 2;

      for (let i = 1; i < entries.length; i += 1) {
        const candidate = entries[i];
        const distanceSquared = (pointX - candidate.x) ** 2 + (pointY - candidate.y) ** 2;
        if (distanceSquared < nearestDistanceSquared) {
          nearest = candidate;
          nearestDistanceSquared = distanceSquared;
        }
      }

      const nearestDistance = Math.sqrt(nearestDistanceSquared);
      if (nearestDistance <= maxDistance) {
        return { x: pointX, y: pointY };
      }

      const vectorX = pointX - nearest.x;
      const vectorY = pointY - nearest.y;
      const scale = maxDistance / nearestDistance;

      return {
        x: nearest.x + vectorX * scale,
        y: nearest.y + vectorY * scale,
      };
    },

    ensureCanvasForPoint(pointX, pointY, edgePadding = 82) {
      let shiftX = 0;
      let shiftY = 0;
      let nextWidth = this.canvasWidth;
      let nextHeight = this.canvasHeight;

      if (pointX < edgePadding) {
        shiftX = edgePadding - pointX;
        nextWidth += shiftX;
      }
      if (pointY < edgePadding) {
        shiftY = edgePadding - pointY;
        nextHeight += shiftY;
      }

      const neededRight = pointX + edgePadding + shiftX;
      const neededBottom = pointY + edgePadding + shiftY;

      if (neededRight > nextWidth) {
        nextWidth = neededRight;
      }
      if (neededBottom > nextHeight) {
        nextHeight = neededBottom;
      }

      if (shiftX !== 0 || shiftY !== 0 || nextWidth !== this.canvasWidth || nextHeight !== this.canvasHeight) {
        const shiftedPositions = {};
        Object.entries(this.positions || {}).forEach(([nodeId, pos]) => {
          shiftedPositions[nodeId] = {
            x: (pos?.x ?? 0) + shiftX,
            y: (pos?.y ?? 0) + shiftY,
          };
        });

        this.positions = shiftedPositions;
        this.canvasWidth = this.clamp(Math.ceil(nextWidth), 1100, MAX_AUTO_CANVAS_WIDTH);
        this.canvasHeight = this.clamp(Math.ceil(nextHeight), 620, MAX_AUTO_CANVAS_HEIGHT);
      }

      return {
        x: pointX + shiftX,
        y: pointY + shiftY,
        shiftX,
        shiftY,
      };
    },

    handleGlobalMouseUp() {
      const hadDragState = Boolean(this.dragState);
      const dragMoved = Boolean(this.dragState && this.dragState.moved);

      if (this.interactionFrameId) {
        cancelAnimationFrame(this.interactionFrameId);
        this.interactionFrameId = null;
      }

      if (this.pendingDragPosition) {
        const { nodeId, x, y } = this.pendingDragPosition;
        this.positions = {
          ...this.positions,
          [nodeId]: { x, y },
        };
        this.pendingDragPosition = null;
      }

      if (this.pendingPanPosition) {
        this.panX = this.pendingPanPosition.panX;
        this.panY = this.pendingPanPosition.panY;
        this.pendingPanPosition = null;
      }

      this.suppressNodeClick = dragMoved;
      this.dragState = null;
      this.panDragState = null;

      if (hadDragState) {
        this.$emit("node-drag-end", { moved: dragMoved });
      }
    },

    getSvgMetrics() {
      const svg = this.$refs.svgRef;
      if (!svg) return null;

      const rect = svg.getBoundingClientRect();
      if (!rect.width || !rect.height) return null;

      const pixelsPerCanvasUnit = Math.min(
        rect.width / this.canvasWidth,
        rect.height / this.canvasHeight
      );
      if (!pixelsPerCanvasUnit) return null;

      const renderedCanvasWidth = this.canvasWidth * pixelsPerCanvasUnit;
      const renderedCanvasHeight = this.canvasHeight * pixelsPerCanvasUnit;
      const offsetX = (rect.width - renderedCanvasWidth) / 2;
      const offsetY = (rect.height - renderedCanvasHeight) / 2;

      return {
        rect,
        scale: 1 / pixelsPerCanvasUnit,
        offsetX,
        offsetY,
      };
    },

    eventToSvgPoint(event) {
      const metrics = this.getSvgMetrics();
      if (!metrics) return null;

      const { rect, scale, offsetX, offsetY } = metrics;

      const svgX = (event.clientX - rect.left - offsetX) * scale;
      const svgY = (event.clientY - rect.top - offsetY) * scale;

      return {
        x: (svgX - this.panX) / this.zoom,
        y: (svgY - this.panY) / this.zoom,
      };
    },

    handleWheelZoom(event) {
      if (!this.hasGraph) return;

      const svg = this.$refs.svgRef;
      if (!svg) return;

      const metrics = this.getSvgMetrics();
      if (!metrics) return;

      const { rect, scale, offsetX, offsetY } = metrics;
      const cursorX = (event.clientX - rect.left - offsetX) * scale;
      const cursorY = (event.clientY - rect.top - offsetY) * scale;

      const sceneX = (cursorX - this.panX) / this.zoom;
      const sceneY = (cursorY - this.panY) / this.zoom;

      const factor = event.deltaY < 0 ? 1.12 : 0.9;
      const nextZoom = this.clamp(this.zoom * factor, MIN_ZOOM, MAX_ZOOM);

      this.zoom = nextZoom;
      this.panX = cursorX - sceneX * this.zoom;
      this.panY = cursorY - sceneY * this.zoom;
      this.hideTooltip();
    },

    showNodeTooltip(node, event) {
      if (this.dragState || !this.showHoverInfo) {
        this.hideTooltip();
        return;
      }
      this.updateTooltip("node", node, event);
    },

    showEdgeTooltip(edge, event) {
      if (this.dragState || !this.showHoverInfo) {
        this.hideTooltip();
        return;
      }
      this.updateTooltip("edge", edge, event);
    },

    updateTooltip(kind, payload, event) {
      if (!this.showHoverInfo) {
        this.hideTooltip();
        return;
      }

      const container = this.$refs.containerRef;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const left = this.clamp(event.clientX - rect.left + 16, 12, Math.max(rect.width - 260, 12));
      const top = this.clamp(event.clientY - rect.top + 16, 12, Math.max(rect.height - 220, 12));

      this.tooltip = {
        kind,
        payload,
        left,
        top,
      };
    },

    hideTooltip() {
      this.tooltip = null;
    },

    objectEntries(source) {
      return Object.entries(source || {})
        .filter(([key]) => !this.isHiddenTooltipKey(key))
        .map(([key, value]) => ({
          key: this.formatTooltipKey(key),
          value: this.formatValue(value),
        }));
    },

    formatTooltipKey(key) {
      const normalized = String(key).toLowerCase();
      if (normalized === "activity_name") return "Activity";
      if (normalized === "workflow_name") return "Workflow";
      if (normalized === "user_name") return "User";
      if (normalized === "relation_category") return "Category";

      return String(key)
        .replace(/_/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase());
    },

    isHiddenTooltipKey(key) {
      if (!key) return false;
      const normalized = String(key).toLowerCase();
      return normalized === "id"
        || normalized.endsWith("_id")
        || normalized === "meta"
        || normalized === "user_username"
        || normalized === "is_start"
        || normalized === "action_name"
        || normalized === "location_name"
        || normalized === "record_type_name";
    },

    formatValue(value) {
      if (value === undefined || value === null || value === "") return "—";
      if (typeof value === "object") return JSON.stringify(value, null, 2);
      return String(value);
    },

    clamp(value, min, max) {
      return Math.min(Math.max(value, min), max);
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/main.scss";

.graph-full-map {
  display: grid;
  gap: 12px;
}

.map-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.toolbar-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.toolbar-toggles {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.hover-info-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
  color: #445261;
  font-weight: 600;
  user-select: none;
}

.hover-info-toggle input {
  margin: 0;
  cursor: pointer;
}

.zoom-indicator {
  margin-left: auto;
  font-size: 0.85rem;
  color: #445261;
  font-weight: 700;
}

.map-hint {
  margin: 0;
  color: #57606a;
  font-size: 0.95rem;
}

.btn-secondary {
  border: 1px solid #cfd7de;
  border-radius: 8px;
  background: #ffffff;
  color: #1f2937;
  cursor: pointer;
  font-weight: 600;
  padding: 8px 12px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
}

.btn-secondary:hover:not(:disabled) {
  background: #f8fafc;
  border-color: #b8c3cc;
}

.btn-secondary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.map-card {
  position: relative;
  border: 1px solid #dfe5eb;
  border-radius: 12px;
  background: linear-gradient(180deg, #ffffff 0%, #f9fbfc 100%);
  min-height: 640px;
  overflow: hidden;
}

.graph-full-map.fullscreen .map-card {
  min-height: calc(90vh - 120px);
}

.map-svg {
  display: block;
  width: 100%;
  height: 640px;
  cursor: grab;
}

.map-svg.is-panning {
  cursor: grabbing;
}

.map-svg.is-interacting .node-circle {
  transition: none;
  filter: drop-shadow(0 5px 10px rgba(15, 23, 42, 0.1));
}

.graph-full-map.fullscreen .map-svg {
  height: calc(90vh - 120px);
}

.state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 640px;
  padding: 24px;
  font-size: 1rem;
}

.graph-full-map.fullscreen .state {
  min-height: calc(90vh - 120px);
}

.muted {
  color: #68737d;
}

.error {
  color: #c62828;
}

.edge-line {
  stroke: #9aa7b2;
  stroke-width: 2.5;
  vector-effect: non-scaling-stroke;
}

.edge-hit {
  stroke: transparent;
  stroke-width: 18;
  cursor: pointer;
  vector-effect: non-scaling-stroke;
}

.edge-label {
  fill: #334155;
  font-size: 12px;
  font-weight: 700;
  paint-order: stroke;
  stroke: rgba(255, 255, 255, 0.95);
  stroke-width: 4px;
  stroke-linejoin: round;
}

.node-group {
  cursor: grab;
}

.node-group.dragging {
  cursor: grabbing;
}

.node-calibration-ring {
  fill: none;
  stroke-width: 4;
  vector-effect: non-scaling-stroke;
  pointer-events: none;
}

.node-calibration-ring.is-within-window {
  stroke: #2f9e44;
}

.node-calibration-ring.is-overdue {
  stroke: #d64545;
}

.node-calibration-ring.is-not-available {
  stroke: #9ca3af;
}

.node-circle {
  stroke-width: 3;
  transition: transform 0.15s ease, filter 0.15s ease;
  filter: drop-shadow(0 8px 18px rgba(15, 23, 42, 0.12));
}

.node-group:hover .node-circle {
  transform: scale(1.03);
  filter: drop-shadow(0 10px 24px rgba(15, 23, 42, 0.16));
}

.node-circle.node-experiment {
  fill: #d7f5e5;
  stroke: #2f855a;
}

.node-circle.node-record {
  fill: #d9ecff;
  stroke: #2563eb;
}

.node-circle.node-instrument {
  fill: #f8ead8;
  stroke: #b7791f;
}

.node-circle.node-material {
  fill: #efe3f7;
  stroke: #7e3fa3;
}

.node-circle.node-unknown {
  fill: #eceff3;
  stroke: #6b7280;
}

.node-circle.is-start {
  stroke: #c17b00;
  stroke-width: 4;
}

.node-name {
  fill: #0f172a;
  font-size: 12px;
  font-weight: 700;
  pointer-events: none;
}

.node-type {
  font-size: 11px;
  font-weight: 600;
  fill: #475569;
  text-transform: capitalize;
}

.graph-tooltip {
  position: absolute;
  z-index: 5;
  width: min(320px, calc(100% - 24px));
  border-radius: 12px;
  background: rgba(15, 23, 42, 0.96);
  color: #f8fafc;
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.28);
  padding: 12px 14px;
  pointer-events: none;
}

.tooltip-title {
  font-size: 0.95rem;
  font-weight: 700;
  margin-bottom: 8px;
}

.tooltip-row {
  display: grid;
  grid-template-columns: 110px 1fr;
  gap: 8px;
  padding: 4px 0;
  align-items: start;
}

.tooltip-key {
  color: #cbd5e1;
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.tooltip-value {
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 0.84rem;
  line-height: 1.35;
}

.node-popup-backdrop {
  position: absolute;
  inset: 0;
  z-index: 8;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.node-popup {
  width: min(860px, calc(100% - 24px));
  max-height: calc(100% - 24px);
  overflow: visible;
  border-radius: 12px;
  background: #ffffff;
  border: 1px solid #dfe5eb;
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.28);
}

.node-popup-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid #e5eaf0;
}

.node-popup-header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.node-popup-title {
  font-weight: 700;
  color: #111827;
}

.node-popup-body {
  padding: 14px 16px;
  display: grid;
  gap: 14px;
  overflow: visible;
}

.node-popup-section h4 {
  margin: 0 0 8px;
  font-size: 0.95rem;
  color: #1f2937;
}

.node-popup-row,
.node-popup-kv-row {
  display: grid;
  grid-template-columns: max-content 1fr;
  gap: 2px;
  padding: 4px 0;
}

.node-popup-key,
.node-popup-kv-key {
  color: #475569;
  font-size: 0.82rem;
  font-weight: 700;
  white-space: nowrap;
}

.node-popup-key::after,
.node-popup-kv-key::after {
  content: ":";
  margin-left: 2px;
}

.node-popup-value,
.node-popup-kv-value {
  color: #111827;
  font-size: 0.88rem;
  line-height: 1.35;
  white-space: pre-wrap;
  word-break: break-word;
}

.node-popup-mdlist {
  border: 1px solid #e5eaf0;
  border-radius: 10px;
  padding: 10px 12px;
  margin-bottom: 10px;
}

.node-popup-mdlist-title {
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 6px;
}

.node-popup-subtle {
  color: #64748b;
  font-size: 0.83rem;
  margin-bottom: 8px;
}

.node-popup-kv {
  display: grid;
  gap: 2px;
}

.edge-edit-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.edge-plus-btn {
  min-width: 40px;
  width: 40px;
  height: 40px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.edge-relation-select {
  flex: 1;
  min-width: 0;
  border: 1px solid #cfd7de;
  border-radius: 8px;
  padding: 8px 10px;
  background: #fff;
}

.edge-create-form {
  margin-top: 10px;
  display: grid;
  gap: 8px;
}

.edge-create-input {
  width: 100%;
  border: 1px solid #cfd7de;
  border-radius: 8px;
  padding: 8px 10px;
  background: #fff;
}

.edge-create-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

@media (max-width: 768px) {
  .map-toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .toolbar-actions {
    width: 100%;
    justify-content: space-between;
    flex-wrap: wrap;
  }

  .toolbar-toggles {
    flex-wrap: wrap;
  }

  .zoom-indicator {
    width: 100%;
    text-align: right;
  }

  .map-card,
  .map-svg,
  .state {
    min-height: 520px;
    height: 520px;
  }

  .tooltip-row {
    grid-template-columns: 1fr;
    gap: 2px;
  }

  .node-popup-row,
  .node-popup-kv-row {
    grid-template-columns: 1fr;
    gap: 2px;
  }
}
</style>
